import { Box, Typography } from '@mui/material';
import TextStyle from '@tiptap/extension-text-style';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useEffect, useState } from 'react';

interface IMark {
  type: string;
  attrs?: Record<string, unknown>;
}

interface INode {
  type: string;
  content?: INode[];
  attrs?: Record<string, unknown>;
  text?: string;
  marks?: IMark[];
}

interface IDocNode {
  type: 'doc';
  content: INode[];
}

interface RichTextReaderProps {
  richText: string;
  rawText: string;
}

function extractTextFromNode(node: INode): string {
  if (node.type === 'text') {
    return node.text || '';
  }

  if (node.content && Array.isArray(node.content)) {
    return node.content.map(extractTextFromNode).join('');
  }

  return '';
}

let truncateNodes: (
  nodes: INode[],
  maxChars: number
) => { nodes: INode[]; charCount: number };

function truncateNode(node: INode, remaining: number): INode | null {
  if (node.type === 'text') {
    const text = node.text || '';
    return {
      ...node,
      text: text.slice(0, remaining),
    };
  }

  if (node.content && Array.isArray(node.content)) {
    const { nodes } = truncateNodes(node.content, remaining);
    if (nodes.length > 0) {
      return {
        ...node,
        content: nodes,
      };
    }
  }

  return null;
}

truncateNodes = (
  nodes: INode[],
  maxChars: number
): { nodes: INode[]; charCount: number } => {
  const result = nodes.reduce<{ nodes: INode[]; charCount: number }>(
    (acc, node) => {
      if (acc.charCount >= maxChars) return acc;

      const nodeText = extractTextFromNode(node);
      const nodeLength = nodeText.length;

      if (acc.charCount + nodeLength <= maxChars) {
        acc.nodes.push(node);
        acc.charCount += nodeLength;
      } else {
        const remaining = maxChars - acc.charCount;
        if (remaining > 0) {
          const truncatedNode = truncateNode(node, remaining);
          if (truncatedNode) {
            acc.nodes.push(truncatedNode);
            acc.charCount = maxChars;
          }
        }
      }

      return acc;
    },
    { nodes: [], charCount: 0 }
  );

  return result;
};

export function RichTextReader({
  richText,
  rawText,
}: RichTextReaderProps): JSX.Element | null {
  const [expanded, setExpanded] = useState(false);
  const showToggle = rawText && rawText.length > 1200;

  const editor = useEditor({
    extensions: [StarterKit, TextStyle],
    editable: false,
  });

  useEffect(() => {
    if (!editor) return;

    let fullContent: IDocNode;

    try {
      fullContent = richText
        ? (JSON.parse(richText) as IDocNode)
        : {
            type: 'doc',
            content: [
              {
                type: 'paragraph',
                content: [{ type: 'text', text: rawText || '' }],
              },
            ],
          };
    } catch {
      fullContent = {
        type: 'doc',
        content: [
          {
            type: 'paragraph',
            content: [{ type: 'text', text: rawText || '' }],
          },
        ],
      };
    }

    if (!expanded) {
      const { nodes: truncatedNodes } = truncateNodes(
        fullContent.content,
        1200
      );
      const truncatedContent: IDocNode = {
        type: 'doc',
        content: truncatedNodes,
      };
      editor.commands.setContent(truncatedContent);
    } else {
      editor.commands.setContent(fullContent);
    }
  }, [editor, richText, rawText, expanded]);

  if (!editor) return <Box />;

  return (
    <Box
      sx={{
        '& .ProseMirror': {
          p: {
            margin: 0,
            textAlign: 'justify',
            width: '100%',
          },
          ':focus-visible': { outline: 'none' },
        },
      }}
    >
      <EditorContent editor={editor} readOnly />

      {showToggle && (
        <Typography
          sx={{ mt: 1, cursor: 'pointer', fontWeight: 600 }}
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? 'Mostrar menos' : 'Mostrar mais'}
        </Typography>
      )}
    </Box>
  );
}
